import React, { useCallback, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileSpreadsheet, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { cn } from '../../lib/utils';
import { HsmContactBatch, HsmCsvImportResult } from '../../types';

export interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batches: HsmContactBatch[];
  batchesLoading: boolean;
  onImport: (file: File, batchId?: number, batchName?: string) => Promise<{
    success: boolean;
    error?: string;
    result?: HsmCsvImportResult;
  }>;
  onRefresh: () => void;
}

type BatchOption = 'new' | 'existing';

export function CsvImportDialog({
  open,
  onOpenChange,
  batches,
  batchesLoading,
  onImport,
  onRefresh
}: CsvImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [batchOption, setBatchOption] = useState<BatchOption>('new');
  const [newBatchName, setNewBatchName] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [importing, setImporting] = useState(false);

  // Result state
  const [importResult, setImportResult] = useState<HsmCsvImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  const resetForm = useCallback(() => {
    setSelectedFile(null);
    setBatchOption('new');
    setNewBatchName('');
    setSelectedBatchId('');
    setImportResult(null);
    setImportError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  }, [onOpenChange, resetForm]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill batch name from filename (remove extension)
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      setNewBatchName(baseName);
      // Reset any previous results
      setImportResult(null);
      setImportError(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setNewBatchName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setImporting(true);
    setImportError(null);
    setImportResult(null);

    try {
      let batchId: number | undefined;
      let batchName: string | undefined;

      if (batchOption === 'existing' && selectedBatchId) {
        batchId = parseInt(selectedBatchId, 10);
      } else if (batchOption === 'new' && newBatchName.trim()) {
        batchName = newBatchName.trim();
      }

      const response = await onImport(selectedFile, batchId, batchName);

      if (response.success && response.result) {
        setImportResult(response.result);
        // Refresh contacts and batches after successful import
        onRefresh();
      } else {
        setImportError(response.error || 'Erro desconhecido ao importar');
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Erro ao importar CSV');
    } finally {
      setImporting(false);
    }
  }, [selectedFile, batchOption, selectedBatchId, newBatchName, onImport, onRefresh]);

  const canImport = selectedFile && (
    (batchOption === 'new' && newBatchName.trim()) ||
    (batchOption === 'existing' && selectedBatchId)
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar Contatos via CSV
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <span>Importe contatos de um arquivo CSV. O arquivo deve conter colunas com os seguintes nomes:</span>
            <ul className="text-xs list-disc list-inside ml-2 space-y-0.5">
              <li><strong>phone</strong> ou <strong>telefone</strong> (obrigatorio) - formato E.164 (+5519999999999)</li>
              <li><strong>name</strong> ou <strong>nome</strong> - nome do contato</li>
              <li><strong>empresa</strong> ou <strong>company</strong> - nome da empresa</li>
              <li><strong>cpf_cnpj</strong> ou <strong>cpf</strong> ou <strong>cnpj</strong> - documento</li>
              <li><strong>email</strong> - endereco de email</li>
            </ul>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>Arquivo CSV</Label>
            {!selectedFile ? (
              <div
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                  'hover:border-primary hover:bg-muted/50',
                  'border-muted-foreground/25'
                )}
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Clique para selecionar um arquivo CSV
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-green-600" />
                  <span className="text-sm truncate">{selectedFile.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={importing}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Batch Options */}
          {selectedFile && (
            <>
              <div className="space-y-2">
                <Label>Destino do Lote</Label>
                <Select
                  value={batchOption}
                  onValueChange={(value: BatchOption) => setBatchOption(value)}
                  disabled={importing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma opcao" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Criar novo lote</SelectItem>
                    <SelectItem value="existing">Usar lote existente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {batchOption === 'new' ? (
                <div className="space-y-2">
                  <Label htmlFor="batch-name">Nome do Novo Lote</Label>
                  <Input
                    id="batch-name"
                    value={newBatchName}
                    onChange={(e) => setNewBatchName(e.target.value)}
                    placeholder="Ex: Campanha Dezembro 2024"
                    disabled={importing}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Selecione o Lote</Label>
                  <Select
                    value={selectedBatchId}
                    onValueChange={setSelectedBatchId}
                    disabled={importing || batchesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={batchesLoading ? 'Carregando...' : 'Selecione um lote'} />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map((batch) => (
                        <SelectItem key={batch.id} value={batch.id.toString()}>
                          {batch.name} ({batch.contactCount} contatos)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Importacao concluida!</span>
              </div>
              <div className="text-sm space-y-1">
                <p><strong>Lote:</strong> {importResult.batchName}</p>
                <p><strong>Contatos criados:</strong> {importResult.created}</p>
                {importResult.duplicates > 0 && (
                  <p className="text-yellow-700 dark:text-yellow-400">
                    <strong>Duplicados (marcados como inativos):</strong> {importResult.duplicates}
                  </p>
                )}
                {importResult.skippedNumbers.length > 0 && (
                  <div className="mt-2">
                    <p className="text-orange-700 dark:text-orange-400 font-medium">
                      Numeros ignorados ({importResult.skippedNumbers.length}):
                    </p>
                    <ul className="text-xs mt-1 max-h-20 overflow-y-auto bg-background/50 rounded p-2">
                      {importResult.skippedNumbers.map((num, idx) => (
                        <li key={idx} className="text-muted-foreground">{num}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Error */}
          {importError && (
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Erro na importacao</span>
              </div>
              <p className="text-sm mt-1 text-red-600 dark:text-red-400">{importError}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={importing}
          >
            {importResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!importResult && (
            <Button
              onClick={handleImport}
              disabled={!canImport || importing}
            >
              {importing ? 'Importando...' : 'Importar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
